<template>
    <div class="break-count-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ $t("report.agentBreakTime") }}</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('message.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('message.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('message.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="chart-wrapper">
            <div v-if="!tableMode">
                <DoubleBarChart
                    :title="$t('report.agentBreakTime')"
                    :xAxisData="getXAxisData()"
                    :yAxisData="getYAxisData()"
                    :series="getSeries()"
                />
            </div>

            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import log2 from "../../../utils/log2";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { mapState } from "vuex";
import fscreen from "fscreen";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import calendar, { getPieSeries } from "../old/data/calendar";
import UserStatusBarChart from "../../Pages/Chatbot/pages/reports/charts/UserStatusBarChart.vue";
import UserStatusCalendarPieChart from "../../Pages/Chatbot/pages/reports/charts/UserStatusCalendarPieChart.vue";
import SupsisButton from "../../Pages/Settings/components/SupsisButton.vue";
import UserBreakCountBarChart from "../../Pages/Chatbot/pages/reports/charts/UserBreakCountBarChart.vue";

const log = log2("BreakCount");
export default Vue.extend({
    name: "BreakCount",
    components: {
        UserBreakCountBarChart,
        SupsisButton,
        UserStatusCalendarPieChart,
        UserStatusBarChart,
        ChartDoddleIcon,
        DoubleBarChart,
        TableView,
        IconButton,
        StatCard,
    },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
        loading: {
            type: Boolean,
            default: true,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            selectedCalendarUser: null,
            calendarTitle: "",
            tableMode: false,
            loadingInstance: null,
            fullScreen: false,
            cancels: [],
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
        }),
    },
    methods: {
        calculateSumBreakCountSeries() {
            const users = this.reportUsers();
            return users.map((user) => {
                let breakCount = 0;
                Object.values(this.data).forEach((xs) => {
                    const userSession = xs.find((x) => x.userId === user._id);
                    log("userSession", { userSession, user, xs });
                    if (userSession) {
                        breakCount += userSession.status.breakCount;
                    }
                });
                return {
                    name: "Break Count",
                    type: "bar",
                    emphasis: {
                        focus: "series",
                    },
                    data: [breakCount],
                };
            });
        },
        getUserBreakCount(userId) {
            let breakCount = 0;
            Object.values(this.data).forEach((xs) => {
                const userSession = xs.find((x) => x.userId === userId);
                log("userSession", { userSession, userId, xs });
                if (userSession) {
                    breakCount += userSession.status.breakCount;
                }
            });
            return breakCount;
        },
        getYAxisData() {
            const users = this.reportUsers();
            return {
                type: "category",

                data: users.map((user) => {
                    return user.name;
                }),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                },
            };
        },

        getXAxisData() {
            const users = this.reportUsers();
            const breakCounts = users.map((user) => this.getUserBreakCount(user._id));
            const max = _.max(breakCounts);

            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                },
            };
        },
        getSeries() {
            const items = this.data || [];
            const users = this.reportUsers();
            log("getSeries.items", items);
            const series = [
                {
                    colorBy: "data",
                    showBackground: true,
                    type: "bar",
                    label: {
                        position: "right",
                        show: true,
                    },
                    data: users.map((x) => this.getUserBreakCount(x._id)),
                },
            ];

            return series;
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".break-count-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        reportUsers() {
            let allUserIds = Object.values(this.data)
                .map((xs) => xs.map((x) => x.userId))
                .flat();
            allUserIds = _.uniq(allUserIds);
            let users = this.users.filter((x) => allUserIds.includes(x._id));
            users = _.orderBy(users, (q) => q.name.toLowerCase(), "desc");
            log("reportUsers", users);
            return users;
        },
        getCols() {
            return [
                {
                    title: this.$t("report.user"),
                    dataIndex: "user",
                    key: "user",
                },
                {
                    title: this.$t("report.breakCount"),
                    dataIndex: "breakCount",
                    key: "breakCount",
                },
            ];
        },
        getRows() {
            const users = this.reportUsers();
            let items = users.map((x) => {
                return {
                    user: x.name || "Unknown User",
                    breakCount: this.getUserBreakCount(x._id) || 0,
                };
            });
            return items;
        },

        exportToExcel() {
            const users = this.reportUsers();
            let items = users.map((x) => {
                x.breakCount = this.getUserBreakCount(x._id);
                return x;
            });
            log("exportToExcel.items", items);

            const excelData = items.map((item) => {
                return {
                    User: item.name || "Unknown User",
                    "Break Count": item.breakCount || 0,
                };
            });
            expertToExcel(excelData, `agent_break_counts.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".break-count-chart"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            console.log("fullscreenCallback", fscreen.fullscreenElement);
            if (fscreen.fullscreenElement !== null) {
                this.fullScreen = true;
            } else {
                this.fullScreen = false;
            }
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        data: {
            handler(val) {
                log.info("data changed", val);
            },
            deep: true,
        },
        filters: {
            handler(val) {
                log.info("filters changed", val);
            },
            deep: true,
        },
    },
});
</script>
<style lang="scss">
.break-count-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.break-count-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .supsis-button {
        &.btnLeft {
            position: absolute;
            left: 0;
            top: 50%;
        }

        &.btnRight {
            position: absolute;
            right: 0;
            top: 50%;
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
